/// <reference types="cypress" />
import i18n, { InitOptions } from "i18next";
import { getLocale } from "@utils/meristemContext";

/**
 * Core language translations
 * */
import en from "./en/translations.json";
import de from "./de/translations.json";
import es from "./es/translations.json";

/**
 * Locale-specific translations
 *
 * NOTE: All translations should be first added to the "core"
 * language files. These files are only meant to override
 * locale-specific differences (i.e. Color vs Colour).
 * See docs/translations.md for more information.
 * */
import enAU from "./en-AU/translations.json";
import enCA from "./en-CA/translations.json";
import enGB from "./en-GB/translations.json";
import enIE from "./en-IE/translations.json";
import enMX from "./en-MX/translations.json";
import enNZ from "./en-NZ/translations.json";
import enPK from "./en-PK/translations.json";
import enSG from "./en-SG/translations.json";
import enUS from "./en-US/translations.json";

export enum LanguageCode {
  EN = "en",
  ES = "es",
  DE = "de",
  JA = "ja",
  KO = "ko",
}

const locale = getLocale();

const settings: InitOptions = {
  lng: locale,
  fallbackLng: "en",
  saveMissing: true,
  debug:
    __NODE_ENV__ !== "production" &&
    __NODE_ENV__ !== "test" &&
    typeof window !== "undefined",
  interpolation: {
    escapeValue: false,
  },
  react: {
    // Enables keeping the name of simple nodes (e.g. <br/>) in translations instead of indexed keys
    transSupportBasicHtmlNodes: true,
    // TODO: Drop ul and li once question sets support richer data structures
    transKeepBasicHtmlNodesFor: ["br", "strong", "i", "p", "ul", "li"],
  },
  resources: {
    en,
    es,
    de,
    "en-AU": enAU,
    "en-CA": enCA,
    "en-GB": enGB,
    "en-IE": enIE,
    "en-MX": enMX,
    "en-NZ": enNZ,
    "en-PK": enPK,
    "en-SG": enSG,
    "en-US": enUS,
  },
};

settings.missingKeyHandler = (lng, ns, key, fallback) => {
  // Adding this condition to throw an error during jest and cypress test runs to catch any missing translation keys
  const isTestingEnv =
    __NODE_ENV__ === "test" || typeof Cypress !== "undefined";

  // Some strings have intentional fallbacks that are safe to use. Only throw if the fallback string is the key name, which indicates a lookup failure.
  const hasGracefulFallback = key !== fallback;
  if (hasGracefulFallback) {
    return;
  }

  if (isTestingEnv) {
    throw new Error(
      `Key missing: Lang: ${lng}, Key: ${key}, Fallback: ${fallback}`
    );
  }

  if (__NODE_ENV__ === "production") {
    (window as any).mixpanel.track("MissingTranslationError", {
      i18nLanguage: lng,
      key,
      fallback,
    });
  } else {
    // Soft failure to reduce Sentry noise in development
    // eslint-disable-next-line no-console
    console.error(
      "Key Missing!",
      "Language:",
      lng.join(", "),
      "Key:",
      key,
      "Fallback:",
      fallback
    );
  }
};

i18n.init(settings);

export default i18n;
